import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import useOnClickOutside from 'application/hooks/useOnClickOutside';
import { ExpandMoreIcon } from 'application/assets';
import Input from '../Input';
import { DropdownProps } from './models';
import {
  DropdownStyled,
  OptionsList,
  OptionItemWrapper,
  OptionItem,
} from './styles';
import { useAppDispatch, useAppSelector } from 'application/hooks/redux';
import { LocationsActions } from 'application/store/reducers/Locations/actions';

/**
 * Functional component representing a location dropdown.
 *
 * @param label - The label of the dropdown.
 * @param id - The unique identifier for the dropdown.
 * @param placeholder - The placeholder text to display when no option is selected.
 * @param searchPlaceholder - The placeholder text to display in the search input.
 * @param value - The current value of the dropdown.
 * @param onChange - The callback function to handle changes to the dropdown's value.
 * @param disabled - Whether the dropdown should be disabled or not.
 * @param withSearch - Whether the dropdown should include a search input or not.
 */
const LocationDropdown: FC<DropdownProps> = ({
  label,
  id,
  placeholder,
  searchPlaceholder,
  value,
  onChange,
  disabled,
  withSearch,
}) => {
  const dispatch = useAppDispatch();
  const ref = useRef(null);
  const refSection = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [searchItem, setSearchItem] = useState('');

  const { locations } = useAppSelector((state) => state.locations);

  useEffect(() => {
    dispatch(LocationsActions.getLocations({}));
  }, []);

  // Hook to close dropdown when clicking outside
  useOnClickOutside(ref, () => setIsActive(false));

  // Memoized list of options based on search input
  const optionList = useMemo(() => {
    if (!searchItem) return locations;
    return locations.filter((item) =>
      item.name.toLocaleLowerCase().includes(searchItem.toLocaleLowerCase()),
    );
  }, [locations, searchItem]);

  // Function to get the current value's name
  const getValue = () => {
    const currentOption = locations.find(
      (item) => String(item.id) === String(value),
    );
    return currentOption?.name;
  };

  const disabledDropdown = !locations?.length || disabled;

  useEffect(() => {
    // Listener to scroll to a specific point in the dropdown when active
    const listener = () => {
      if (refSection?.current && isActive) {
        setTimeout(() => {
          const wrapper = document.querySelector('.children-wrapper');

          wrapper?.scrollTo({
            top: 1000,
            behavior: 'smooth',
          });
        }, 200);
      }
    };

    // Adding listener on click
    document.addEventListener('click', listener);

    // Removing listener on cleanup
    return () => {
      document.removeEventListener('click', listener);
    };
  }, [refSection, isActive]);

  return (
    <DropdownStyled isActive={isActive} disabled={disabledDropdown} ref={ref}>
      {label && <label htmlFor={id}>{label}</label>}
      <section
        ref={refSection}
        onMouseDown={() => !disabledDropdown && setIsActive(!isActive)}
      >
        <>
          {!value.length ? <span>{placeholder}</span> : <h2>{getValue()}</h2>}
        </>
        <ExpandMoreIcon />
      </section>
      {isActive && (
        <OptionsList className="optionList">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
            />
          )}
          {optionList.map((item) => (
            <OptionItem
              key={item.name}
              onClick={() => {
                onChange(item.id);
                setIsActive(false);
              }}
              selected={value === String(item.id)}
            >
              {item.name}
            </OptionItem>
          ))}
        </OptionsList>
      )}
    </DropdownStyled>
  );
};

export default LocationDropdown;
